import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, DatePicker, Dropdown } from "antd"
import { useMatch, useNavigate } from "react-router-dom"
import { oldRoutes } from "router/old-routes"
import useForm from "hooks/useForm"
import useSearchFilter, { toAccessSearchString } from "hooks/useSearchFilter"
import { TRIP_FILTERS } from "constants/trip"
import styles from "layoutsV2/client/GuestHeader/style.module.css"
import GooglePlacesInput from "modules/google/GooglePlacesInput"
import { SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { setGuestCount, setCalendarDates } from "store/trips"
const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"

SearchTripsAntd.propTypes = {
  className: PropTypes.string
}

export default function SearchTripsAntd({ className }) {
  const navigate = useNavigate()
  const placeInputRef = useRef()
  const isTripsPage = useMatch(oldRoutes.tripsPath())
  const [params, updateSearchFilters] = useSearchFilter({ accessAttributes: TRIP_FILTERS })
  const [form, changeHandler, submitHandler, submitCallback, reset, isChanged] = useForm(params)
  const dispatch = useDispatch()
  const guests = useSelector((state) => state.trips.guests)
  const { t } = useTranslation()
  const [adults, setAdults] = useState(guests.adults || 0)
  const [children, setChildren] = useState(guests.children || 0)

  useEffect(() => {
    setAdults(guests.adults || 0)
    setChildren(guests.children || 0)
  }, [guests.adults, guests.children])

  const placeChangedHandler = ({
    target: {
      value,
      coordinates: { lat, lng }
    }
  }) => {
    changeHandler({ target: { name: "address", value } })
    changeHandler({ target: { name: "lat", value: lat } })
    changeHandler({ target: { name: "lng", value: lng }  })
  }

  submitCallback(() => {
    const search = toAccessSearchString(form, TRIP_FILTERS)
    navigate({ pathname: oldRoutes.tripsPath(), search })
  })

  useEffect(() => {
    if (placeInputRef.current) placeInputRef.current.value = form.address ? form.address : ""
  }, [form.address])
  useEffect(() => {
    if (!isTripsPage) {
      reset()
      updateSearchFilters({ guest_count: "", from_date: "", to_date: "" })
      if (placeInputRef.current)  placeInputRef.current.value = ""
    }
  }, [isTripsPage])

  const handleGuestChange = (type, guestType) => {
    let newAdults = adults;
    let newChildren = children;

    if (type === "increase" && guestType === "adults") {
      newAdults = adults + 1;
    } else if (type === "increase") {
      newChildren = children + 1;
    } else if (guestType === "adults") {
      newAdults = Math.max(0, adults - 1);
    } else {
      newChildren = Math.max(0, children - 1);
    }

    const totalGuests = newAdults + newChildren;

    // Update Redux store
    dispatch(setGuestCount({ adults: newAdults, children: newChildren }));

    // Update form values (so submit uses guest_count)
    changeHandler({ target: { name: "adults", value: newAdults } });
    changeHandler({ target: { name: "children", value: newChildren } });
    changeHandler({ target: { name: "guest_count", value: totalGuests } });

    // keep local UI state in sync
    setAdults(newAdults);
    setChildren(newChildren);
  }

  const handleClearGuests = () => {
    dispatch(setGuestCount({ adults: 0, children: 0 }))
    changeHandler({ target: { name: "adults", value: 0 } })
    changeHandler({ target: { name: "children", value: 0 } })
    changeHandler({ target: { name: "guest_count", value: 0 } })
    setAdults(0)
    setChildren(0)
  }

  const classes = [styles.antdSearchTrips, "flex-wrap"]
  if (className) classes.push(className)

  const guestDropdownContent = (
    <div className={styles.guestDropdownMenu}>
      <div className={styles.guestCategory}>
        <span>{t("filterTags.adults")}</span>
        <div className={styles.guestCounter}>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("decrease", "adults")} disabled={adults <= 0}>
            −
          </Button>
          <span>{adults}</span>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("increase", "adults")}>
            +
          </Button>
        </div>
      </div>

      <div className={styles.guestCategory}>
        <span>{t("filterTags.children")}</span>
        <div className={styles.guestCounter}>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("decrease", "children")} disabled={children <= 0}>
            −
          </Button>
          <span>{children}</span>
          <Button shape="circle" size="small" onClick={() => handleGuestChange("increase", "children")}>
            +
          </Button>
        </div>
      </div>

      <div className={styles.clearLink}>
        <Button type="link" onClick={handleClearGuests}>
          {t("filterTags.clear")}
        </Button>
      </div>
    </div>
  )

  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const from = dates[0].format("YYYY-MM-DD")
      const to = dates[1].format("YYYY-MM-DD")
      dispatch(
        setCalendarDates({
          from_date: from,
          to_date: to
        })
      )
      // mirror into form so submit constructs correct search string
      changeHandler({ target: { name: "from_date", value: from } })
      changeHandler({ target: { name: "to_date", value: to } })
    } else {
      dispatch(
        setCalendarDates({
          from_date: "",
          to_date: ""
        })
      )
      changeHandler({ target: { name: "from_date", value: "" } })
      changeHandler({ target: { name: "to_date", value: "" } })
    }
  }

  return (
    <form className={classes.join(" ")} onSubmit={submitHandler}>
      <div className={styles.searchBox}>
        <div className={styles.inputWrapper}>
          <SearchOutlined className={styles.icon} />
          <GooglePlacesInput
            placeholder={t("filterTags.any_location")}
            className={styles.input}
            defaultValue={form.address}
            onChange={placeChangedHandler}
            wrapperProps={{ className: styles.inputInner }}
            ref={placeInputRef}
          />
        </div>
        <div className={styles.divider} />
        <Dropdown className={styles.guestDropdown} overlay={guestDropdownContent} trigger={["click"]} placement="bottomLeft">
          <Button className={styles.input}>
            {adults + children > 0 ? `${adults + children} ${t("filterTags.guests")}` : t("filterTags.guests")}
          </Button>
        </Dropdown>

        <div className={styles.divider} />

        <div className={styles.inputWrapper}>
          <RangePicker
            className={styles.clientRangePicker}
            format={dateFormat}
            onChange={handleDateChange}
            placeholder={[t("global.from"), t("global.to")]}
          />
        </div>

        <Button type="primary" htmlType="submit" className={styles.searchButton} disabled={!isChanged}>
          {t("global.search")}
        </Button>
      </div>
    </form>
  )
}
